/**
 * Scroll Tracking Hook
 * Tracks how far users scroll on a page and reports to analytics
 */
import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function useScrollTracking(pageName) {
  const trackedMilestones = useRef(new Set());
  const sessionId = useRef(null);
  const startTime = useRef(Date.now());

  // Generate or get session ID
  useEffect(() => {
    let sid = sessionStorage.getItem('scroll_session_id');
    if (!sid) {
      sid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('scroll_session_id', sid);
    }
    sessionId.current = sid;
    startTime.current = Date.now();
    trackedMilestones.current = new Set();
  }, [pageName]);

  const trackScroll = useCallback(async (milestone) => {
    if (trackedMilestones.current.has(milestone)) return;
    trackedMilestones.current.add(milestone);

    const timeOnPage = Math.round((Date.now() - startTime.current) / 1000);
    
    try {
      await axios.post(`${API}/api/analytics/scroll`, {
        page: pageName,
        milestone: milestone,
        session_id: sessionId.current,
        time_on_page: timeOnPage,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        screen_width: window.innerWidth
      });
    } catch (err) {
      // Silent fail - don't break UX for analytics
      console.debug('Analytics tracking failed:', err);
    }
  }, [pageName]);

  useEffect(() => {
    if (!pageName) return;

    // Track page view (0% = entered page)
    trackScroll(0);

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      
      if (docHeight <= 0) return;
      
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      // Track milestones: 25%, 50%, 75%, 100%
      if (scrollPercent >= 25 && !trackedMilestones.current.has(25)) {
        trackScroll(25);
      }
      if (scrollPercent >= 50 && !trackedMilestones.current.has(50)) {
        trackScroll(50);
      }
      if (scrollPercent >= 75 && !trackedMilestones.current.has(75)) {
        trackScroll(75);
      }
      if (scrollPercent >= 95 && !trackedMilestones.current.has(100)) {
        trackScroll(100);
      }
    };

    // Throttle scroll handler
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    
    // Track time on page when leaving
    const handleBeforeUnload = () => {
      const timeOnPage = Math.round((Date.now() - startTime.current) / 1000);
      // Use sendBeacon for reliable tracking on page exit
      navigator.sendBeacon(
        `${API}/api/analytics/exit`,
        JSON.stringify({
          page: pageName,
          session_id: sessionId.current,
          time_on_page: timeOnPage,
          max_scroll: Math.max(...trackedMilestones.current)
        })
      );
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [pageName, trackScroll]);

  return null;
}
